/**
 * PILLAR STANDARDIZATION CONSTANTS
 * Single source of truth for all pillar names and IDs across the application
 * Standardizes on human-readable format: "Account Reconciliation", "Journal Entry Automation", etc.
 */

// CANONICAL PILLAR NAMES - Use these everywhere
export const CANONICAL_PILLAR_NAMES = {
  ACCOUNT_RECONCILIATION: "Account Reconciliation",
  JOURNAL_ENTRY_AUTOMATION: "Journal Entry Automation",
  INTERCOMPANY: "Intercompany",
  CLOSE_MANAGEMENT: "Close Management"
} as const;

const CANONICAL_PILLAR_LOOKUP: Record<string, string> = Object.values(CANONICAL_PILLAR_NAMES).reduce(
  (lookup, name) => {
    lookup[name.toLowerCase()] = name;
    return lookup;
  },
  {} as Record<string, string>
);

// All pillar definitions with descriptions
export const STANDARD_PILLARS = [
  {
    name: CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION,
    description: "Automate balance sheet reconciliation processes",
    category: "Financial Close",
    id: "account-reconciliation" // Legacy compatibility only
  },
  {
    name: CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
    description: "Streamline journal entry creation and approval",
    category: "Financial Close", 
    id: "journal-entry-automation" // Legacy compatibility only
  },
  {
    name: CANONICAL_PILLAR_NAMES.INTERCOMPANY,
    description: "Optimize intercompany transactions and eliminations",
    category: "Financial Close",
    id: "intercompany" // Legacy compatibility only
  },
  {
    name: CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT,
    description: "Accelerate period-end close processes",
    category: "Financial Close",
    id: "close-management" // Legacy compatibility only
  }
] as const;

// Legacy format mappings for migration/compatibility
export const LEGACY_PILLAR_MAPPINGS = {
  // Slug to canonical name
  "account-reconciliations": CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION,
  "account-reconciliation": CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION,
  "journal-entries": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
  "journal-entry": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
  "intercompany": CANONICAL_PILLAR_NAMES.INTERCOMPANY,
  "close-management": CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT,

  // Alternative names to canonical name
  "Account Reconciliations": CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION,
  "Journal Entries": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
  "Journal Entry": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
  "Close Management and Orchestration": CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT,
  "Account reconciliation": CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION,
  "Journals": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION,
  "Close management": CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT,

  // PillarBreakdown keys from chartDataExtractor (used by backend)
  "Intercompany Transactions": CANONICAL_PILLAR_NAMES.INTERCOMPANY,
  "Accruals": CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION, // Accruals are part of Journal Entry Automation

  // Old UI names (from SolutionPillarSelector before fix)
  "Intercompany Matching": CANONICAL_PILLAR_NAMES.INTERCOMPANY,
  "Accruals + Reclass": CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT,
  "Close Orchestration": CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT
} as const;

const LEGACY_PILLAR_LOOKUP: Record<string, string> = Object.entries(LEGACY_PILLAR_MAPPINGS).reduce(
  (lookup, [key, value]) => {
    lookup[key.toLowerCase()] = value;
    return lookup;
  },
  {} as Record<string, string>
);

// Pain point options by canonical pillar name
export const PILLAR_PAIN_POINTS = {
  [CANONICAL_PILLAR_NAMES.ACCOUNT_RECONCILIATION]: [
    "Manual prep", 
    "Excel dependency", 
    "Exception backlog", 
    "Late reconciliations", 
    "Complex variance analysis"
  ],
  [CANONICAL_PILLAR_NAMES.JOURNAL_ENTRY_AUTOMATION]: [
    "Multiple reviewers", 
    "High adjustment volume", 
    "Late journal entries", 
    "Manual approvals", 
    "Supporting documentation gaps"
  ],
  [CANONICAL_PILLAR_NAMES.INTERCOMPANY]: [
    "Disputes", 
    "Reconciliation gaps", 
    "Lack of automation", 
    "Currency complexities", 
    "Timing differences"
  ],
  [CANONICAL_PILLAR_NAMES.CLOSE_MANAGEMENT]: [
    "Last-minute changes", 
    "Manual dependencies", 
    "Poor coordination", 
    "Missing deadlines", 
    "Communication bottlenecks"
  ]
} as const;

/**
 * Normalize any pillar reference to canonical format
 * @param pillarReference - Any pillar name, ID, or slug
 * @returns Canonical pillar name or original if not found
 */
export function normalizePillarName(pillarReference: string): string {
  if (!pillarReference) return pillarReference;

  const trimmed = pillarReference.trim();
  const lower = trimmed.toLowerCase();

  // Case-insensitive lookup for canonical names first
  if (CANONICAL_PILLAR_LOOKUP[lower]) {
    return CANONICAL_PILLAR_LOOKUP[lower];
  }

  // Fall back to case-insensitive legacy mappings
  if (LEGACY_PILLAR_LOOKUP[lower]) {
    return LEGACY_PILLAR_LOOKUP[lower];
  }

  return trimmed;
}

/**
 * Get all canonical pillar names as array
 */
export function getAllCanonicalPillarNames(): string[] {
  return Object.values(CANONICAL_PILLAR_NAMES);
}

/**
 * Validate if a pillar name is canonical
 */
export function isCanonicalPillarName(pillarName: string): boolean {
  return Object.values(CANONICAL_PILLAR_NAMES).includes(pillarName as any);
}

/**
 * Migrate array of pillar references to canonical format
 */
export function migrateToCanonicalPillars(pillars: string[]): string[] {
  return pillars.map(pillar => normalizePillarName(pillar));
}